import type { CustomerDetail, CustomerOrderView } from "@kopi/contracts";
import { Badge } from "@kopi/ui/components/badge";
import { Button } from "@kopi/ui/components/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@kopi/ui/components/card";
import { ConfirmButton } from "@kopi/ui/components/confirm-button";
import { DataTable, type DataTableColumn } from "@kopi/ui/components/data-table";
import { toast } from "@kopi/ui/components/sonner";
import { StatCard } from "@kopi/ui/components/stat-card";
import { useInfiniteQuery, useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { CalendarIcon, PencilIcon, ReceiptIcon, WalletIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { Page, PageHeader } from "@/components/page-header";
import { CustomerDialog, type CustomersT, customerError } from "@/customers/customer-dialog";
import { customerOrdersQuery, customerQuery, customersKey } from "@/customers/queries";
import { customers } from "@/i18n/messages/customers";
import { type useI18n, useMessages } from "@/i18n/provider";
import { api, unwrap } from "@/lib/api";
import { CHANNEL_KEYS } from "@/lib/channels";

export const Route = createFileRoute("/_authed/customers/$customerId")({
  staticData: {
    section: "workspace",
    parent: { label: "nav.customers", to: "/customers" },
    crumb: "nav.customer",
  },
  // No loader: it would pull the customer queries into the shared route tree (cashier bundle).
  component: CustomerPage,
});

function orderColumns(
  t: CustomersT,
  format: ReturnType<typeof useI18n>["format"],
): DataTableColumn<CustomerOrderView>[] {
  return [
    {
      id: "receipt",
      header: t("customers.receipt"),
      meta: { card: "title" },
      cell: ({ row }) => <span className="font-medium tabular-nums">{row.original.receiptNumber}</span>,
    },
    {
      id: "date",
      header: t("customers.date"),
      cell: ({ row }) => (
        <time dateTime={row.original.completedAt}>{format.dateTime(row.original.completedAt)}</time>
      ),
    },
    { id: "outlet", header: t("customers.outlet"), cell: ({ row }) => row.original.outletName },
    { id: "type", header: t("customers.type"), cell: ({ row }) => t(CHANNEL_KEYS[row.original.orderType]) },
    {
      id: "items",
      header: t("customers.items"),
      meta: { className: "md:w-20" },
      cell: ({ row }) => <span className="tabular-nums">{row.original.itemCount}</span>,
    },
    {
      id: "total",
      header: t("customers.total"),
      meta: { className: "md:w-36 md:text-right" },
      cell: ({ row }) => <span className="tabular-nums">{format.money(BigInt(row.original.total))}</span>,
    },
  ];
}

/** Promotion consent: the answer in force, a way to record a new one, and the trail of answers. */
function ConsentCard(props: { customer: CustomerDetail }) {
  const { customer } = props;
  const { t, format } = useMessages(customers);
  const queryClient = useQueryClient();
  const record = useMutation({
    mutationFn: async (granted: boolean) =>
      unwrap(
        await api.v1.customers({ customerId: customer.id }).consents.post({ purpose: "marketing", granted }),
      ),
    onSuccess: async (_, granted) => {
      await queryClient.invalidateQueries({ queryKey: customersKey });
      toast.success(t(granted ? "customers.consentRecorded" : "customers.consentWithdrawn"));
    },
    onError: (error) => toast.error(customerError(t, error)),
  });
  const active = customer.status === "active";
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("customers.consentTitle")}</CardTitle>
        <CardDescription>{t("customers.consentHint")}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Badge variant={customer.marketingConsent ? "success" : "outline"}>
            {t(customer.marketingConsent ? "customers.consentOn" : "customers.consentOff")}
          </Badge>
          {active &&
            (customer.marketingConsent ? (
              <ConfirmButton
                variant="outline"
                title={t("customers.withdrawTitle")}
                description={t("customers.withdrawHint")}
                confirmLabel={t("customers.withdraw")}
                disabled={record.isPending}
                onConfirm={() => record.mutate(false)}
              >
                {t("customers.withdraw")}
              </ConfirmButton>
            ) : (
              <Button
                type="button"
                variant="outline"
                disabled={record.isPending}
                onClick={() => record.mutate(true)}
              >
                {t("customers.grant")}
              </Button>
            ))}
        </div>
        <div className="flex flex-col gap-2">
          <h3 className="font-medium text-sm">{t("customers.consentHistory")}</h3>
          {customer.consents.length === 0 ? (
            <p className="text-muted-foreground text-sm">{t("customers.noConsentHistory")}</p>
          ) : (
            // At most 20 entries (CODING_STANDARDS §4 exception); the audit log keeps every one.
            <ul className="flex flex-col gap-1 text-sm">
              {customer.consents.map((consent) => (
                <li key={consent.id} className="text-muted-foreground">
                  {t("customers.consentEntry", {
                    answer: t(consent.granted ? "customers.granted" : "customers.declined"),
                    source: t(`customers.source.${consent.source}`),
                    time: format.dateTime(consent.recordedAt),
                    name: consent.recordedBy,
                  })}
                  {consent.outletName && ` · ${consent.outletName}`}
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/** One customer for a manager (R1-20): contact, promotion consent and purchase history. */
function CustomerPage() {
  const { customerId } = Route.useParams();
  const { t, format } = useMessages(customers);
  const { data: customer } = useSuspenseQuery(customerQuery(customerId));
  const orders = useInfiniteQuery(customerOrdersQuery(customerId));
  const columns = useMemo(() => orderColumns(t, format), [t, format]);
  const [editing, setEditing] = useState(false);
  const { stats } = customer;

  return (
    <Page>
      <PageHeader
        eyebrow={t("customers.eyebrow")}
        title={customer.displayName}
        subtitle={[customer.phone, customer.email].filter(Boolean).join(" · ") || t("customers.noContact")}
        actions={
          customer.status === "active" && (
            <Button type="button" variant="outline" onClick={() => setEditing(true)}>
              <PencilIcon />
              {t("customers.edit")}
            </Button>
          )
        }
      />
      {customer.mergedIntoId && (
        <div
          role="status"
          className="flex flex-wrap items-center gap-3 rounded-xl border bg-card p-4 text-sm"
        >
          <span>{t("customers.mergedInto")}</span>
          <Button type="button" variant="outline" asChild>
            <Link to="/customers/$customerId" params={{ customerId: customer.mergedIntoId }}>
              {t("customers.openMerged")}
            </Link>
          </Button>
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard
          icon={<ReceiptIcon />}
          label={t("customers.orders")}
          value={format.quantity(String(stats.orderCount))}
          note={t("customers.ordersNote")}
        />
        <StatCard
          icon={<WalletIcon />}
          label={t("customers.spend")}
          value={format.money(BigInt(stats.totalSpend))}
        />
        <StatCard
          icon={<CalendarIcon />}
          label={t("customers.lastVisit")}
          value={stats.lastOrderAt ? format.date(new Date(stats.lastOrderAt)) : t("customers.never")}
        />
      </div>
      <ConsentCard customer={customer} />
      <section className="flex flex-col gap-3">
        <h2 className="font-semibold text-lg">{t("customers.history")}</h2>
        <DataTable
          paging="pages"
          label={t("customers.history")}
          columns={columns}
          query={orders}
          getRowId={(order) => order.orderId}
          empty={{ title: t("customers.noOrders"), description: t("customers.noOrdersHint") }}
        />
      </section>
      {editing && <CustomerDialog customer={customer} onClose={() => setEditing(false)} />}
    </Page>
  );
}
